import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import moment from 'moment';
import Frame from 'react-frame-component';
import { Media, Modal, Card, Button } from 'react-bootstrap';
import TeleScript from '../../Components/TeleScript/TeleScript';
import getCMSObject from '../../utils/getCMSObject';
/**
 * ## Purpose
 *
 * Allows a specific coupon to popup in a modal and display
 * specific coupon instructions.   The user is also able to
 * print the coupon so that they can redeem it at a retailer.
 *
 * ## Notes
 *
 * The coupon redeemption instructions can be customized via
 * Maestro in the LPS area via a CMS tag (this is the redeem prop)
 * if that is not null, we use it - if not - we will default
 * to generic redeem instructions located in the CMS.
 *
 * ## Important
 *
 * The print-coupon.css file MUST be in the Public folder of the
 * web-app code that uses this component!
 *
 * APIs used:  None - data comes from parent component via props.
 */
var Coupon = function (_a) {
    var lottery_logo = _a.lottery_logo, reward_id = _a.reward_id, prize_name = _a.prize_name, prize_description = _a.prize_description, redeem = _a.redeem, className = _a.className, expiration = _a.expiration, state = _a.state, Url = _a.Url, barcodeHost = _a.barcodeHost, _b = _a.linkType, linkType = _b === void 0 ? 'link' : _b;
    var couponContent = getCMSObject('data.components.teleScripts.couponContent.jsonBlock');
    var _c = useState(false), show = _c[0], setShow = _c[1];
    var _d = useState(false), redeemed = _d[0], setRedeemed = _d[1];
    var _e = useState(false), isExpired = _e[0], setIsExpired = _e[1];
    var _f = useState(false), failed = _f[0], setFailed = _f[1];
    useEffect(function () {
        if (state === 'complete') {
            setRedeemed(true);
        }
        else if (state === 'failed') {
            setFailed(true);
        }
        if (Math.floor(Date.now() / 1000) > expiration) {
            setIsExpired(true);
        }
    }, []);
    var handleClose = function () {
        setShow(false);
    };
    var handleShow = function () {
        setShow(true);
    };
    var handleKeyShow = function (event) {
        if (event.key === 'Enter') {
            setShow(true);
        }
    };
    var handleKeyClose = function (event) {
        if (event.key === 'Enter') {
            setShow(false);
        }
    };
    var printCoupon = function (iFrameId) {
        // TODO: We use this in conjunction with `react-frame-component` in webapp-ctl-src (src/Components/Rewards/Coupon.js). We should refactor this so we aren't accesing a property that shouldn't exist on the document, but need to do so carefully to ensure we aren't breaking CTL
        var thisFrame = document['frames']
            ? document['frames'][iFrameId]
            : document.getElementById(iFrameId);
        var iframeWindow = (thisFrame === null || thisFrame === void 0 ? void 0 : thisFrame.contentWindow) || thisFrame;
        thisFrame === null || thisFrame === void 0 ? void 0 : thisFrame.focus();
        iframeWindow.print();
        return false;
    };
    var barcodeUrl = function (barcodeUrlString, barcodeHostString) {
        if (barcodeUrlString === void 0) { barcodeUrlString = ''; }
        if (barcodeUrlString && typeof barcodeUrlString === 'string') {
            // eslint-disable-line
            return barcodeUrlString.startsWith('http') ? barcodeUrlString : barcodeHostString + barcodeUrlString;
        }
        else {
            return '';
        }
    };
    var descriptionHTML = function () {
        return { __html: prize_description };
    };
    var expirationDate = moment.unix(expiration).format('MM/DD/YYYY');
    var buildCouponContent = function () {
        return (_jsx(Card, { className: "shadow text-center ".concat(className), children: _jsxs(Card.Body, { children: [_jsx("div", { className: 'row', children: _jsx("div", { className: 'col d-flex justify-content-center', children: _jsxs(Media, { className: 'align-items-center', children: [_jsx("img", { className: 'lottery-logo', src: lottery_logo, alt: 'Lottery Logo' }), _jsx(Media.Body, { children: _jsx("h2", { className: 'text-left', children: prize_name }) })] }) }) }), _jsx("div", { className: 'row', children: _jsx("div", { className: 'col prize-description', children: _jsx("div", { className: 'mb-3', dangerouslySetInnerHTML: descriptionHTML() }) }) }), _jsxs("div", { className: 'row', children: [_jsx("div", { className: 'col-md d-print-none', children: _jsx(Button, { className: 'print-coupon-btn', variant: 'theme', onClick: function () { return printCoupon("printCouponIframe-".concat(reward_id)); }, children: _jsx(TeleScript, { line: couponContent === null || couponContent === void 0 ? void 0 : couponContent.printMessage, children: "Print Coupon" }) }) }), _jsx("div", { className: 'col-md', children: _jsxs("p", { className: 'text-center', children: [_jsx("strong", { children: _jsx(TeleScript, { line: couponContent === null || couponContent === void 0 ? void 0 : couponContent.expirationDateMessage, children: "Coupon Expiration" }) }), _jsx("br", {}), expirationDate] }) }), _jsx("div", { className: 'col-md', children: _jsx("img", { className: 'mx-auto d-block img-fluid', src: barcodeUrl(Url, barcodeHost), alt: 'coupon barcode' }) })] })] }) }));
    };
    var buildCouponElements = function () {
        var redeemInstructions = getCMSObject('data.couponRedeemInstructions');
        if (redeemed) {
            return (_jsx("p", { children: _jsx(TeleScript, { line: couponContent === null || couponContent === void 0 ? void 0 : couponContent.couponRedeemedText, children: "Redeemed" }) }));
        }
        else if (isExpired) {
            return (_jsx("p", { children: _jsx(TeleScript, { line: couponContent === null || couponContent === void 0 ? void 0 : couponContent.couponExpiredText, children: "Expired" }) }));
        }
        else if (failed) {
            return (_jsx("p", { children: _jsx(TeleScript, { line: couponContent === null || couponContent === void 0 ? void 0 : couponContent.couponFailedText, children: "Failed" }) }));
        }
        return (_jsxs("div", { className: 'coupon', children: [linkType === 'link' ? (_jsx("a", { className: 'd-block', onKeyDown: handleKeyShow, onClick: handleShow, children: _jsx(TeleScript, { line: couponContent === null || couponContent === void 0 ? void 0 : couponContent.couponLinkText, children: "View Coupon" }) })) : (_jsx("button", { className: 'btn theme-btn theme-secondary', onKeyDown: handleKeyShow, onClick: handleShow, children: _jsx(TeleScript, { line: couponContent === null || couponContent === void 0 ? void 0 : couponContent.couponLinkText, children: "View Coupon" }) })), _jsxs(Modal, { dialogClassName: 'coupon-modal', backdropClassName: 'coupon-modal-backdrop', size: 'lg', show: show, onHide: handleClose, centered: true, children: [_jsx(Modal.Header, { children: _jsx("a", { className: 'modal-close-element', title: 'Close', tabIndex: 0, role: 'button', onKeyDown: handleKeyClose, onClick: handleClose, children: "X" }) }), _jsxs(Modal.Body, { children: [_jsx("div", { className: 'row', children: _jsx("div", { className: 'col-sm-10 offset-sm-1', children: buildCouponContent() }) }), _jsx("div", { className: 'row', children: _jsx("div", { className: 'col my-4 coupon-storage', children: _jsx("strong", { children: _jsx(TeleScript, { line: couponContent === null || couponContent === void 0 ? void 0 : couponContent.couponStorageText, children: "Coupon will be stored in the coupon history." }) }) }) })] })] }), _jsxs(Frame, { className: 'hidden', id: "printCouponIframe-".concat(reward_id), head: _jsx("link", { rel: 'stylesheet', href: '/css/print-coupon.css' }), children: [buildCouponContent(), _jsx("div", { className: 'row', children: _jsx("div", { className: 'col my-4', children: redeem ? (_jsx("div", { dangerouslySetInnerHTML: { __html: redeem } })) : (_jsx(_Fragment, { children: (redeemInstructions === null || redeemInstructions === void 0 ? void 0 : redeemInstructions.contentHTML) ? _jsx("div", { dangerouslySetInnerHTML: { __html: redeemInstructions === null || redeemInstructions === void 0 ? void 0 : redeemInstructions.contentHTML } }) : null })) }) })] })] }));
    };
    return _jsx(_Fragment, { children: buildCouponElements() });
};
Coupon.defaultProps = {
    lottery_logo: '',
    reward_id: '',
    prize_name: '',
    prize_description: '',
    redeem: '',
    state: '',
    Url: '',
    barcodeHost: '',
    linkType: 'link',
};
export default Coupon;
